import { isError } from 'lodash-es';
import { captureException } from '@sentry/vue';
import type { CaptureContext } from '@sentry/types';

/**
 * @description We can receive any value as a cause, not only errors
 *
 * try catch of .catch return any or unknown as an error
 */
type ErrOrUnknown = Error | unknown;
type WithCauseParams = {
  cause?: ErrOrUnknown;
  /**
   * type CaptureContext from '@sentry/types' is not suitable for the client's captureException,
   * so wee need to get the correct type from the function arguments
   * @see https://github.com/getsentry/sentry-javascript/issues/5409#issuecomment-1181948620
   */
  captureContext?: CaptureContext;
};

export type ErrorWithCause = Error & {
  cause?: ErrOrUnknown;
};

const appendCause = <T extends Error>(
  mainError: string | T,
  cause?: ErrOrUnknown,
) => {
  const err: ErrorWithCause = isError(mainError)
    ? mainError
    : new Error(mainError);

  err.cause = cause;
  return err;
};

/**
 * @description Method to create an error with cause for better logging in Sentry
 *
 * @param mainError - description of an error to be displayed as a title in Sentry
 *
 * @param cause - is an error that occurred
 *
 * @param captureContext - Sentry captureContext
 */
const captureExWithCause = <T extends Error>(
  mainError: string | T,
  { cause, captureContext }: WithCauseParams = {},
) => {
  const errorWithCause = appendCause(mainError, cause);

  captureException(errorWithCause, captureContext);
};

export const sentryLib = { captureExWithCause };
